// tslint:disable:max-classes-per-file

import { Button, Icon } from "armstrong-react";
import classNames from "classnames";
import moment from "moment";
import React from "react";
import _ from "underscore";

import * as DateUtil from "@travellocal/utils/dateHelpers";
import { Avatar, LinkButton } from "../../../core/atoms/atoms";
import { EnumHelper, PlanningStageEnumHelper } from "@travellocal/utils/enumHelper";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { RoutesInvoker as PortalRoutes } from "./../../../portal/routesInvoker";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { ShowMoreHeightFade } from "@travellocal/ui";
import { Tooltip } from "./../tooltip";
import { neverHere } from "@travellocal/utils/tsHelpers";

import "./message.scss";

interface Props {
  userContext: UserType;
  message: IMessage;
  enquiry: IEnquiryOutput;
  onReplyClick?: () => void;
  latestReplyFromLto: number;
  latestReplyFromCustomer: number;
}

const logOpenMessageEvent = () => GoogleAnalyticsHelper.logEvent("Show detail", "Click", "Messages tab");

export class Message extends React.PureComponent<Props> {
  private renderMessageBody = (message: IMessage) => {
    switch (message.messageType) {
      case MessageType.InitialEnquiry:
        const initialMessage = message as IInitialEnquiryMessage;
        return <InitialEnquiryMessage message={initialMessage} enquiry={this.props.enquiry} userContext={this.props.userContext} />;
      case MessageType.Itinerary:
        const itinMessage = message as IEnquiryItineraryMessage;
        return <ItineraryMessageBody message={itinMessage} enquiry={this.props.enquiry} userContext={this.props.userContext} />;
      case MessageType.Text:
      default:
        return (
          <ShowMoreHeightFade threshold={350} className="text-message" onOpen={logOpenMessageEvent}>
            <div dangerouslySetInnerHTML={{ __html: message.text }}></div>
          </ShowMoreHeightFade>
        );
    }
  }

  private renderMeta = () => {
    const { message } = this.props;

    if (message.toCustomer) {
      let fromName = message.fromUser?.firstName;
      if (message.fromUser?.location != null) {
        fromName = `${fromName} in ${message.fromUser.location.parent.name}`
      }

      const avatarUrl = message.fromUser?.image?.thumbnailUrl ?? "/images/anon.jpg";

      return (
        <div className="meta">
          <Avatar source={avatarUrl} />
          <p><span className="name">{fromName}</span></p>
        </div>
      );
    }
    else {
      const fromName = this.props.userContext === UserType.Customer ? "You" : this.props.enquiry.customer.firstName;
      const avatarUrl = "/images/anon.jpg";
      return (
        <div className="meta">
          <Avatar source={avatarUrl} />
          <p><span className="name">{fromName}</span></p>
        </div>
      );
    }
  }

  public render() {
    let sourceIndicator: React.ReactElement;
    const { message, onReplyClick } = this.props;

    if (message.source === MessageSource.Email && message.toCustomer === false) {
      sourceIndicator = <span title="Sent via email"> via <Icon icon={Icon.Icomoon.envelop2} /></span>;
    }

    // Only show the reply button on the latest message
    let renderReplyButton = false;
    if (onReplyClick && this.props.userContext === UserType.Customer && this.props.latestReplyFromLto === message.id ||
      this.props.userContext === UserType.LtoEmployee && this.props.latestReplyFromCustomer === message.id) {
      renderReplyButton = true;
    };

    // Show messages from the user on the right (match Whatsapp convention)
    const isSentByCurrentUserType = ((message.toCustomer && this.props.userContext != UserType.Customer)
      || (!message.toCustomer && this.props.userContext == UserType.Customer))
    const fromClassName = isSentByCurrentUserType ? "self" : "other";

    return (
      <div className={classNames("message-wrap", `message-wrap--${fromClassName}`)}>
        {this.renderMeta()}

        <div className="message">
          {this.renderMessageBody(message)}

          {message.toCustomer &&
            <div className="lto-contact-details">
              {this.props.message.fromUser?.skypeId &&
                <p><em>Skype me:</em> <a href={`skype:${this.props.message.fromUser.skypeId}?call`}>{this.props.message.fromUser.skypeId}</a></p>
              }
              {this.props.message.fromUser?.phoneNumber &&
                <p><em>Call me:</em> <a href={`tel:${this.props.message.fromUser.phoneNumber}`}>{this.props.message.fromUser.phoneNumber}</a></p>
              }
            </div>
          }

          <div className="message-info">
            {renderReplyButton && (
              <Button
                className="message-wrap__reply"
                onClick={onReplyClick}>
                Reply <Icon icon={Icon.Icomoon.undo2} />
              </Button>
            )}

            <span className="sent-time"><Icon icon={Icon.Icomoon.checkmark3} />
              <time>{DateUtil.formatDateTime(message.sentTime, "h:mma Do MMM YY")}</time>{sourceIndicator}
            </span>
          </div>
        </div>
      </div>
    );
  }
}

class ItineraryMessageBody extends React.Component<{ userContext: UserType, enquiry: IEnquiryOutput, message: IEnquiryItineraryMessage }> {
  private canBook = () => {
    const { userContext, message, enquiry } = this.props;

    if (enquiry == null || !enquiry.isConnected || userContext !== UserType.Customer) {
      return false;
    }

    if (message.enquiryItineraryQuoteId !== enquiry.currentQuoteId) {
      return false;
    }

    const status = enquiry.enquiryStatus;
    if (status === EnquiryStatus.Booked || status === EnquiryStatus.FullyPaid || status === EnquiryStatus.Cancelled || status === EnquiryStatus.Complete) {
      return false;
    }

    return true;
  }

  private getItineraryLink = (quoteId: number) => {
    const { enquiry, userContext } = this.props;
    switch (userContext) {
      case UserType.Administrator:
      case UserType.LtoEmployee:
        return PortalRoutes.enquiryItinerary(enquiry.id, quoteId);
      case UserType.Customer:
        return WebRoutes.customerEnquiryItinerary(enquiry.id, quoteId);
      default:
        neverHere(userContext, `Unkown user type ${userContext}`)
    }
  }

  public render() {
    const { message } = this.props;
    const canBook = this.canBook();

    return (
      <div className="itinerary-message">
        <ShowMoreHeightFade
          threshold={350}
          onOpen={logOpenMessageEvent}
        >
          <div dangerouslySetInnerHTML={{ __html: message.text }}></div>
        </ShowMoreHeightFade>

        <div className="message-actions">
          <LinkButton
            className="view-itinerary"
            to={this.getItineraryLink(message.enquiryItineraryQuoteId)}
            icomoon={Icon.Icomoon.map2}
            onClick={() => GoogleAnalyticsHelper.logEvent("View itinerary", "Click", "Messages tab")}>
            View itinerary
          </LinkButton>

          {canBook &&
            <LinkButton
              className="secure-trip"
              to={WebRoutes.customerEnquiryMakePayment(message.enquiryId)}
              icomoon={Icon.Icomoon.power2}
              onClick={() => GoogleAnalyticsHelper.logEvent("Book now", "Click", "Messages tab")}>
              Book now
            </LinkButton>
          }
        </div>
      </div>
    );
  }
}

class InitialEnquiryMessage extends React.Component<{ userContext: UserType, enquiry: IEnquiryOutput, message: IInitialEnquiryMessage }> {
  private getAgeMessageArray(): string[] {
    const message = this.props.message;

    const ageMessageArray: string[] = [];
    switch (message.version) {
      case 1:
        if (message.numberOfAdults) {
          ageMessageArray.push(`${message.numberOfAdults} adult(s)`);
        }
        if (message.numberOfTeenagers) {
          ageMessageArray.push(`${message.numberOfTeenagers} teenager(s)`);
        }
        if (message.numberOfChildren) {
          ageMessageArray.push(`${message.numberOfChildren} children`);
        }
        if (message.numberOfInfants) {
          ageMessageArray.push(`${message.numberOfInfants} infant(s)`);
        }
        break;
      case 2:
      case 3:
        const groupedAgeRanges = _.groupBy(message.pax.ageRanges, "name");
        for (const ageRange of Object.keys(groupedAgeRanges)) {
          ageMessageArray.push(`${ageRange} years (${groupedAgeRanges[ageRange].length}x)`);
        }
    }

    return ageMessageArray;
  }

  public render() {
    const enquiry = this.props.enquiry;
    const message = this.props.message;
    const showSalesTips = this.props.userContext != UserType.Customer;
    const ageMessageArray = this.getAgeMessageArray();
    const heading = enquiry.isConnected ? `Enquiry to ${enquiry.lto.name}` : `Your enquiry details`;

    return (
      <div className="initial-enquiry-message">
        <header>
          <h1>{heading}</h1>
          {message.version === 1 && showSalesTips &&
            <p className="legacy-message">
              This enquiry was submitted with an older version of our enquiry form, so it won't contain information such as themes or planning stage.
            </p>
          }
        </header>
        <div className="details-2-cols">
          <div>
            <h2>Location</h2>
            <p>{message.location ? message.location.name || "-" : "-"}</p>
          </div>
          <div>
            <h2>Pax</h2>
            <p>{ageMessageArray.join(", ")}</p>
          </div>
          <div>
            <h2>Budget (pp) {showSalesTips &&
              <Tooltip text="This includes TravelLocal's commission. If the budget is too low - reply to the customer saying what a minimum budget is.">
                <Icon icon={Icon.Icomoon.question4} />
              </Tooltip>
            }</h2>
            <p>{`${message.budgetPerPerson ? `${message.budgetPerPerson} ${enquiry.currency.code}` : `Needs advice (${enquiry.currency.code})`}`}</p>
          </div>
          <div>
            <h2>Accommodation type {showSalesTips &&
              <Tooltip text="Accommodation standard - customers can choose simple, boutique/ character, deluxe, a mixture">
                <Icon icon={Icon.Icomoon.question4} />
              </Tooltip>
            }</h2>
            <p>{EnumHelper.getTripType(message.tripType)}</p>
          </div>
          <div >
            <h2>Independence {showSalesTips &&
              <Tooltip text="Guiding level - customer can enter Fully guided/Partially guided/Independent/I don't know.">
                <Icon icon={Icon.Icomoon.question4} />
              </Tooltip>
            }</h2>
            <p>{EnumHelper.getIndependenceLevel(message.independenceLevel)}</p>
          </div>
          <div >
            <h2>Date flexibility {showSalesTips &&
              <Tooltip text="Customer can enter exact date/a particular month/I'm not sure.">
                <Icon icon={Icon.Icomoon.question4} />
              </Tooltip>
            }</h2>
            <p>{EnumHelper.getTravelDateFlexibility(message.travelDateFlexibility)}</p>
          </div>
          <div>
            <h2>Trip length</h2>
            <p>{message.preferredTravelNumberOfDays} days</p>
          </div>
          {message.preferredTravelExactDate &&
            <div>
              <h2>Date</h2>
              <p>{DateUtil.formatDate(message.preferredTravelExactDate, "DD/MM/YYYY")}</p>
            </div>
          }
          {message.travelDateFlexibility === TravelDateFlexibility.RoughDates &&
            <div>
              <h2>Rough date</h2>
              <p>{`${moment().month(message.preferredTravelMonth.month - 1).year(message.preferredTravelMonth.year).format("MM/YYYY")}`}</p>
            </div>
          }
          <div>
            <h2>Phone number</h2>
            <p>{enquiry.customer.phoneNumber || "Not entered"}</p>
          </div>
        </div>
        {message.version !== 1 &&
          <div className="details-2-cols">
            <div>
              <h2>Planning stage {showSalesTips &&
                <Tooltip text={`Customer can select from:
                  ${PlanningStageEnumHelper.toFriendlyName(PlanningStage.InitialResearch)} /
                  ${PlanningStageEnumHelper.toFriendlyName(PlanningStage.MoreAdvice)} /
                  ${PlanningStageEnumHelper.toFriendlyName(PlanningStage.ReadyToBook)}
                  `}>
                  <Icon icon={Icon.Icomoon.question4} />
                </Tooltip>
              }</h2>
              <p>{PlanningStageEnumHelper.toFriendlyName(message.planningStage)}</p>
            </div>
            <div>
              <h2>Travel themes {showSalesTips &&
                <Tooltip text="Customer can select from a variety of themes for their trip">
                  <Icon icon={Icon.Icomoon.question4} />
                </Tooltip>
              }</h2>
              <p>{enquiry.themes.length ? enquiry.themes.map(theme => theme.name).join(", ") : "No themes chosen"}</p>
            </div>
            <div>
              <h2>How did you hear about TravelLocal? {showSalesTips &&
                <Tooltip text="How did the customer find out about us?">
                  <Icon icon={Icon.Icomoon.question4} />
                </Tooltip>
              }</h2>
              <p>{message.recommendedBy || "Not entered"}</p>
            </div>
            <div>
              <h2>Trip idea {showSalesTips &&
                <Tooltip text="If a customer came to the enquiry form from a specific trip idea on the website, it will be shown here">
                  <Icon icon={Icon.Icomoon.question4} />
                </Tooltip>
              }</h2>
              {message.sampleItinerary ?
                <p>Enquired from <a href={`${globalConfig.wwwBaseUrl}${WebRoutes.sampleItinerary(message.sampleItinerary.slug)}`}>{message.sampleItinerary.name}</a></p>
                :
                <p>Did not enquire from a trip idea page</p>
              }
            </div>
          </div>
        }
        <div className="details">
          {message.roomPreferences &&
            <div>
              <h2>Room preferences</h2>
              <p>{message.roomPreferences || "No preference"}</p>
            </div>
          }
          <div className="details">
            <h2>Notes {showSalesTips &&
              <Tooltip text="Pay attention to what the customer says here.">
                <Icon icon={Icon.Icomoon.question4} />
              </Tooltip>
            }</h2>
            <p>{`${message.text || "No notes entered..."}`}</p>
          </div>
        </div>
      </div>
    );
  }
}
